/**
 * DARS Officer Portal — router.
 *
 * Mirrors dars-admin router shape: /login + /forgot-password (auth), /
 * (MainLayout with nested routes for the officer's day). Solution-license
 * guard for `dars`.
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore, useEntitlements } from '@adaptcx/shared'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredSolution: 'dars' },
    children: [
      { path: '',            name: 'Dashboard',      component: () => import('@/views/DashboardView.vue') },

      // ── My Work group ────────────────────────────
      { path: 'schedule',    name: 'MySchedule',     component: () => import('@/views/MyScheduleView.vue') },
      { path: 'cases',       name: 'MyCases',        component: () => import('@/views/CasesView.vue') },
      // v0.8.22 — /cases/:id/edit + /:id/upload need to precede /:id
      // so vue-router matches the specific route first.
      { path: 'cases/:id/edit',   name: 'CaseEdit',   component: () => import('@/views/NewCaseView.vue'), props: true },
      { path: 'cases/:id/upload', name: 'CaseUpload', component: () => import('@/views/CaseUploadView.vue'), props: true },
      // v0.9.0 — CaseDetail is now a dedicated view (full OmniDars port
      // with overview/participants/hearings/documents/notes/timeline
      // tabs). Previously HearingDetailView handled both cases + hearings
      // via a route-name branch; the case-route content there is now dead
      // code slated for cleanup.
      { path: 'cases/:id',   name: 'CaseDetail',     component: () => import('@/views/CaseDetailView.vue'), props: true, meta: { title: 'Case Detail' } },
      // v0.9.0 — Render Decision is now case-scoped (case can have
      // multiple hearings; the decision finalizes the case). Legacy
      // /decisions/:hearingId still routes for back-compat but the
      // new canonical URL is /cases/:id/decision.
      { path: 'cases/:id/decision', name: 'RenderDecision', component: () => import('@/views/RenderDecisionView.vue'), props: true, meta: { title: 'Render Decision' } },
      { path: 'decisions',   name: 'PendingDecisions', component: () => import('@/views/PendingDecisionsView.vue') },
      { path: 'decisions/:hearingId', name: 'RenderDecisionLegacy', component: () => import('@/views/RenderDecisionView.vue'), props: true },

      // ── Calendar group (v0.8.22 — OmniDars port) ────
      { path: 'calendar/add',       name: 'AddEvent',    component: () => import('@/views/AddEventView.vue') },
      { path: 'calendar/reminder',  name: 'AddReminder', component: () => import('@/views/AddEventView.vue') },
      { path: 'calendar/event/:id', name: 'EditEvent',   component: () => import('@/views/AddEventView.vue'), props: true },

      // ── Pre-Hearing Prep (v0.8.23 — OmniDars port) ────
      { path: 'pre-hearing/:packetId', name: 'PreHearing',
        component: () => import('@/views/PreHearingView.vue'), props: true },

      // ── Hearings group ────────────────────────────
      { path: 'hearings',    name: 'MyHearings',     component: () => import('@/views/HearingsView.vue') },
      { path: 'hearings/approvals', name: 'PendingApprovals', component: () => import('@/views/HearingApprovalsView.vue') },
      // v0.9.3 — HearingRoomView deleted; /hearings/:id/room legacy URL
      // redirects into HearingDetail (Detail is now the canonical single
      // view for both pre-hearing and live workspace).
      { path: 'hearings/:id/room', redirect: to => ({ name: 'HearingDetail', params: { id: to.params.id } }) },
      { path: 'hearings/:id',name: 'HearingDetail',  component: () => import('@/views/HearingDetailView.vue'), props: true },

      // ── Settings ──────────────────────────────────
      { path: 'profile',     name: 'ProfileSettings',component: () => import('@/views/profile/ProfileSettingsView.vue') },

      // ── Quick Actions ─────────────────────────────
      { path: 'templates',   name: 'Templates',      component: () => import('@/views/TemplatesView.vue') },
      { path: 'contacts',    name: 'Contacts',       component: () => import('@/views/ContactsView.vue') },
      { path: 'actions/ad-hoc-hearing', name: 'StartAdhocHearing',
        component: () => import('@/views/ScheduleHearingView.vue') },
      { path: 'actions/schedule-hearing', name: 'ScheduleHearing',
        component: () => import('@/views/ScheduleHearingView.vue') },
      { path: 'actions/new-case', name: 'NewCase',
        component: () => import('@/views/NewCaseView.vue') },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    await authStore.fetchUser()
    if (!authStore.isAuthenticated) {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }
  next()
})

router.beforeEach(async (to, _from, next) => {
  const requiredSolution = to.meta.requiredSolution as string | undefined
  if (!requiredSolution) return next()
  const { isSolutionLicensed, loaded, load } = useEntitlements()
  if (!loaded.value) await load()
  if (isSolutionLicensed(requiredSolution)) return next()
  next({ name: 'Login', query: { solutionLocked: requiredSolution } })
})

export default router
